import { useCallback, useState } from 'react';

import { useWebPushSubscriptionCreateMutation } from '../../common/external/bambi-api/bambiApi';
import formatServerError from '../../common/util/serverErrorFormatter';
import {
  selectWebPushPermissionState,
  setWebPushPermissionState,
} from './notifications.slice';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY as string;

/**
 * Upserts the device's push subscription for the authenticated user. The
 * backend keys on endpoint, so this also reassigns a shared browser's
 * subscription to whoever is signed in now.
 */
export function useUpsertPushSubscription() {
  const [createSubscription] = useWebPushSubscriptionCreateMutation();

  return useCallback(
    async (subscription: PushSubscription) => {
      const json = subscription.toJSON() as {
        endpoint: string;
        keys: { p256dh: string; auth: string };
      };

      // unwrap so a failed API call throws rather than looking like success
      await createSubscription({
        webPushSubscription: {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
        },
      }).unwrap();
    },
    [createSubscription]
  );
}

/**
 * Returns a handler and status for determining if the user has granted
 * permission to send WebPush and Notifications. WebPush and Notifications are
 * tightly coupled in the browser security model, so we combine their permission
 * states here. Both must be granted in order for us to send the push.
 *
 * Permission alone doesn't mean we can push: the device also needs a
 * PushSubscription registered to the current user. `granted` is only reported
 * once that subscription has been upserted; otherwise we report `prompt` so the
 * UI offers registration (which won't re-prompt if permission is granted).
 */
export function usePushPermissionState(): [
  () => Promise<void>,
  PermissionState | undefined,
  boolean,
  string | undefined,
] {
  const dispatch = useAppDispatch();
  const permissionState = useAppSelector(selectWebPushPermissionState);
  const upsertSubscription = useUpsertPushSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const checkPermission = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      const registration = await navigator.serviceWorker.ready;
      const pushPermissionState =
        await registration.pushManager.permissionState({
          userVisibleOnly: true,
          applicationServerKey: VAPID_PUBLIC_KEY,
        });

      const notificationPermissionState = Notification.permission;
      const isPrompt =
        pushPermissionState === 'prompt' ||
        notificationPermissionState === 'default';
      const isDenied =
        pushPermissionState === 'denied' ||
        notificationPermissionState === 'denied';
      const combinedState: PermissionState = (() => {
        if (isPrompt) {
          return 'prompt';
        }
        if (isDenied) {
          return 'denied';
        }
        return 'granted';
      })();

      if (combinedState !== 'granted') {
        dispatch(setWebPushPermissionState(combinedState));
        return;
      }

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        dispatch(setWebPushPermissionState('prompt'));
        return;
      }

      await upsertSubscription(subscription);
      dispatch(setWebPushPermissionState('granted'));
    } catch (e) {
      console.error('Unable to determine permission state', e);
      setError(formatServerError(e));
    } finally {
      setLoading(false);
    }
  }, [dispatch, upsertSubscription, setLoading, setError]);

  return [checkPermission, permissionState, loading, error];
}

/**
 * Returns a handler and status states for registering the WebPush subscription,
 * and requesting Notification permissions. These are effectively paired
 * permissions from the browser's viewpoint. `register` must be called from a
 * user initiated event.
 */
export function useRegisterPushSubscription(): [
  () => Promise<void>,
  boolean,
  string | undefined,
] {
  const upsertSubscription = useUpsertPushSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const register = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !VAPID_PUBLIC_KEY
    ) {
      setLoading(false);
      setError("WebPush isn't supported");
      return;
    }

    try {
      // Trigger permission request to send the device notifications
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setLoading(false);
        setError('WebPush permission not granted');
        return;
      }

      // Once permission is granted, subscribe the user to the WebPush API
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        // userVisibleOnly as false is basically deprecated, this means any push
        // we generate MUST show the user some notification that the push
        // happened
        userVisibleOnly: true,
        applicationServerKey: VAPID_PUBLIC_KEY,
      });

      // Track the device subscription in the API so we can push from it
      await upsertSubscription(subscription);
    } catch (e) {
      // Non-critical — push unavailable, permission denied, or subscription failed
      console.error('Unable to resolve webpush permissions', e);
      setError(formatServerError(e));
    } finally {
      setLoading(false);
    }
  }, [upsertSubscription, setLoading, setError]);

  return [register, loading, error];
}
